import { LangCode } from '../common/api-response.model';
import { AttributeInfoResponse, AttributeValueInfoResponse } from './attribute.model';
import { BrandInfoResponse } from './brand.model';
import { CategoryInfoResponse, CategoryResponse, SubCategoryInfo } from './category.model';
import { Pager } from '../common/pagination.model';
import { TagInfoResponse } from './tag.model';

export interface ProductsResponse extends Pager {
  products: CatalogueInfoResponse[];
  records?: ProductVariant[];
}

export interface ProductsAndFiltersResponse extends Pager {
  productsAndFilters: ProductsAndFiltersInfo;
}

export interface SingleProduct {
  data: ProductDetails;
}

export interface ProductsAndFiltersInfo {
  products: CatalogueInfoResponse[];
  filters: ProductFilterInfo;
}

export interface OneProductResponse {
  product: ProductInfoResponse;
}

export interface CatalogueInfoResponse {
  id: string;
  img: string[]; // It's now an array
  discount: string;
  price: number;
  vat: string;
  type: string;
  title?: string;
  translations: CatalogueInfoTranslation[];
  rating: number;
  noOfReviews: number;
}

export interface CatalogueInfoTranslation {
  langCode: LangCode;
  title: string;
  brandName: string;
  categoryName: string;
}

export interface ProductInfoResponse {
  id: string;
  sku: number;
  price: number;
  stock: string;
  img: string;
  categoryId: number;
  brandId: number;
  warranty: number;
  guaranty: number;
  status: string;
  lowStockLevel: number;
  productType: string;
  parentId: number;
  relatedId: number[];
  discountRate: string;
  startDiscount: string;
  endDiscount: string;
  thumbnailId: string[];
  videoUrl: string;
  shipping: ShippingInfo;
  dimension: DimensionsInfo;
  vat: string;
  translations: ProductTranslationInfo[];
  category: CategoryInfoResponse;
  brand: BrandInfoResponse;

  // Extendable fields
  cost_price: number;
  qty: number;
  search_code: string;
  attributes: (AttributeInfoResponse & { attributeValue: AttributeValueInfoResponse[] })[];
  tags: TagInfoResponse[];
}

interface ShippingInfo {
  cost: number;
  country: string;
  shipping_method: string;
}

interface DimensionsInfo {
  width: number;
  height: number;
  length: number;
  weight: number;
}
interface ProductTranslationInfo {
  langCode: LangCode;
  title: string;
  specification: ProductSpecificationInfo;
  features: string[];
  description: string;

  // Extendable fields
  seo_keyword: string;
  seo_title: string;
  seo_description: string;
}

export interface ProductSpecificationInfo {
  [key: string]: string;
}

export enum StatusEnum {
  Publish = 'publish',
  Draft = 'draft'
}

export interface ProductFilterInfo {
  brands: BrandInfoResponse[];
  colors: ColorInfo[];
  priceRange: PriceRangeInfo;
  subCategories: SubCategoryInfo[];
}

export interface ColorInfo {
  valueId: number;
  translations: {
    id: number;
    valueId: number;
    langCode: LangCode;
    value: string;
    additions: {
      color: string;
    };
  }[];
}

export interface PriceRangeInfo {
  minPrice: number;
  maxPrice: number;
}

// Product Section Response

export interface ProductSectionResponse extends Pager {
  productsSections: SectionProduct[];
}

export interface SectionProduct {
  id: number;
  img?: string[];
  discount?: string;
  price?: number;
  type?: string;
  rating?: number;
  stock: string;
  refurbished: boolean;
  noOfReviews?: number;
  isInWishlist: boolean;
  translations: SectionProductTranslationInfo[]; // Note: I don't prefer this method of handling translations but backend uses it
}

export interface SectionProductTranslationInfo {
  langCode: LangCode;
  title: string;
  brandName: string;
}

// Product Media

export interface MediaProductsResponse extends Pager {
  products: MediaProduct[];
}

export interface MediaProduct {
  id: number;
  videoUrl: string;
  discount: string;
  price: number;
  vat: string;
  type: string;
  isInWishlist: boolean;
  translations: MediaProductTranslation[];
}

export interface MediaProductTranslation {
  langCode: LangCode;
  title: string;
  brandName: string;
}

// new
export interface ProductsListResponse extends Pager {
  records: ProductVariant[];
}
export interface SearchExplainStep {
  phase: string;
  result: string;
  detail: string;
}
export interface SearchExplain {
  query: string;
  steps: SearchExplainStep[];
  timing_ms: {
    detection: number;
    query_build: number;
    first_aggregation: number;
    final_query: number;
    db_fetch: number;
    category_tree: number;
    total: number;
  };
}
export interface ProductsSearchListResponse extends Pager {
  records: ProductVariant[];
  aggregations: ProductSearchAggregations;
  explain?: SearchExplain;
}
export interface ProductSearchAggregations {
  min_price: number;
  max_price: number;
  category_ids: SearchAggregate[];
  brand_ids: SearchAggregate[];
  tag_ids: SearchAggregate[];
  colors: ColorAggregate[];
  primary_colours?: PrimaryColourAggregate[];
  specifications: SpecificationAggregate;
  category_filters?: HierarchicalCategoryFilter[];
}

export interface HierarchicalCategoryFilter {
  id: number;
  name: string;
  slug: string;
  level: number;
  parent_id: number | null;
  count: number;
  is_selected: boolean;
  has_active_children: boolean;
  has_children: boolean;
  children: HierarchicalCategoryFilter[];
  sort_order: number;
}
export interface SearchAggregate {
  id: number;
  count: number;
}
export interface PrimaryColourAggregate {
  slug: string;
  name: string;
  hex_color_code: string;
  count: number;
}

export interface ColorAggregate {
  name: string;
  count: number;
  hexColorCode?: string;
  hex_color_code?: string;

}
export interface SpecificationAggregate {
  count: number;
  keys: SpecificationAggregateKey[];
}
export interface SpecificationAggregateKey {
  name: string;
  count: number;
  values: SpecificationAggregateValue[];
}
export interface SpecificationAggregateValue {
  value: string;
  count: number;
}

export interface Translation {
  __key: string;
  name: string;
}

export interface Category {
  id: string;
  slug: string;
  translation: Translation;
}

export interface Brand {
  id: string;
  slug: string;
  image_url?:string|null;
  translation: Translation;
}

export interface ProductDetails {
  id: string;
  product_type?: string;
  product_condition?: 'new' | 'used' | 'refurbished' | 'open_box' | 'preorder' | 'for_parts' | 'like_new';
  warranty_days?: number;
  guarantee_days?: number;
  category_id: string;
  brand_id: string;
  creator_id: string | null;
  is_refurbished: boolean;
  is_featured: boolean;
  status: string;
  category: Category;
  department?: Category;
  subcategory?: Category;
  brand: Brand;
  product_variants?: ProductVariant[];
  product_tags_m2ms?: any[];
}

export interface ProductVariantGallery {
  product_variant_id: string;
  image_thumbnail_url?: string;
  image_gallery_urls?: string[];
  video_thumbnail_url?: string | null;
  video_source_url?: string | null;
  createdAt?: string;
  updatedAt?: string;
  video_gallery?:{
    url: string;
    thumbnail_url: string;
    title?:string | null;
    description?:string | null;
    duration?:string | null;
    order?:number ;

  }[]
}

export interface ProductVariantTranslation {
  __key: string;
  product_variant_id: string;
  language: string;
  name: string;
  variant_name?: string;
  description?: string;
  simple_color?: string | null;
  simple_country_of_origin?: string | null;
  features?: string[];
  whats_in_box?: string;
  specifications_json?: ProductVariantSpecification[];
  seo_title?: string;
  seo_description?: string;
  seo_keywords?: string;
  seo_slug?: string;
}

export interface ProductVariantSpecification {
  name?: string;
  value?: string;
}

export interface ProductVariantAttribute {
  product_id: string;
  attribute_key_id: string;
  attribute_value_id: string;
  product_variant_id: string;
  attribute_key: {
    id: string;
    slug: string;
    translation: {
      __key: string;
      attribute_key_id: string;
      language: string;
      name: string;
    };
  };
  attribute_value: {
    id: string;
    slug: string;
    hex_color_code: string | null;
    translation: {
      __key: string;
      attribute_value_id: string;
      language: string;
      name: string;
    };
  };
}

export interface ProductVariant {
  id: string;
  product_id: string;
  sku?: string;
  sale_price?: number | null;
  final_sale_price:number;
  regular_price: number;
  partner_sku?: string;
  barcode?: string | null;
  quantity_available?: number;
  quantity_low?: number;
  weight_unit?: string | null;
  weight_value?: number | null;
  produced_date: string | null;
  expiry_date?: string | null;
  is_in_stock?: boolean;
  model_number?: string;
  dimension_length?: number | null;
  dimension_width?: number | null;
  dimension_height?: number | null;
  dimension_unit?:string | null;
  default_thumbnail?: string;
  num_sales?: number;
  cached_review_count?: number;
  cached_average_rating?: number;
  cached_ratings_json?: string | null;
  createdAt?: string;
  updatedAt?: string;
  product: ProductDetails;
  product_type?:string;
  product_variant_gallery: ProductVariantGallery;
  translation: ProductVariantTranslation;
  product_variant_attributes_m2ms?:ProductVariantAttribute[];
}

export interface ProductsAutocompleteResponse {
  data: string[];
}

export interface ProductVariantResponse {
  brands: BrandInfoResponse[];
  categories: CategoryResponse[];
  product_variants: ProductVariantSearchResponse[];

}
interface ProductVariantSearchResponse {
  id: string;
  name: string;
  image_thumbnail_url: string;
  price: number;

}
export interface SearchHistory {
  id: string;
  user_id: string;
  type: SearchHistoryType;
  is_deleted: boolean;
  hits: number;
  query: string;
  display_query_name: string;
  product_variant_id: string | null;
  brand_id: string | null;
  category_id: string | null;
  createdAt: string;   
  updatedAt: string;   
  product_variant: ProductVariant | null; 
  brand: Brand | null;
  category: Category | null;
}

export type SearchHistoryType = 'category' | 'brand' | 'product_variant' | 'query';

export interface ProductSearchInputOrder {
  page: number;
  limit: number;
  query: string;
  sort: 'asc' | 'desc';
  is_search?:boolean;
}
